import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { delay, finalize, map, Subject, takeUntil } from 'rxjs';

import {
  GetEnumValuesDto,
  PhoneBookRecipientRequest,
} from '../../../../core/api/models';
import { PhonebooksService } from '../../../../core/api/services';
import { PhonebookCustomDto } from '../shared/phonebook-custom.model';
import { PhonebookStateService } from '../shared/phonebook-state.service';
import { PhonebookTypeDirective } from '../shared/phonebook-type.directive';
import { EnumToTextConvertorPipe } from './../../../../core/pipes/enum-text.pipe';
import { TdValueModule } from './../../../../shared/global/td-value/td-value.module';
import { PhonebookRowBtnComponent } from './phonebook-row-btn/phonebook-row-btn.component';
import { PhonebookRowCheckboxComponent } from './phonebook-row-checkbox/phonebook-row-checkbox.component';
import { PhonebookRowInputComponent } from './phonebook-row-input/phonebook-row-input.component';
import { PhonebookRowMenuComponent } from './phonebook-row-menu/phonebook-row-menu.component';
import { PhonebookRowNameComponent } from './phonebook-row-name/phonebook-row-name.component';
@Component({
  selector: '[phonebook-row]',
  imports: [
    CommonModule,
    EnumToTextConvertorPipe,
    PhonebookTypeDirective,
    PhonebookRowBtnComponent,
    PhonebookRowCheckboxComponent,
    PhonebookRowNameComponent,
    PhonebookRowMenuComponent,
    PhonebookRowInputComponent,
    TdValueModule,
  ],
  templateUrl: './phonebook-row.component.html',
  styleUrl: '../phonebook.component.scss',
})
export class PhonebookRowComponent {
  //services
  currentService = inject(PhonebooksService);
  phonebookStateService = inject(PhonebookStateService);
  //props
  @Input() level: number;
  @Input() paddingR: number;
  @Input() phonebook: PhonebookCustomDto;
  @Input() PhonebookTypesEnumValues: GetEnumValuesDto[] = [];
  @Input() phonebookRecipients: PhoneBookRecipientRequest[] = [];
  @Input() isShowInputs: boolean = true;
  @Input() isShowFullTable: boolean = false;
  @Input() isShowParentBtn: boolean = true;
  @Input() checked: boolean;
  @Output() operation = new EventEmitter<string>();
  @Output() select = new EventEmitter<PhonebookCustomDto>();
  @Output() changeState = new EventEmitter<PhonebookCustomDto>();
  protected onDestroy$ = new Subject<void>();
  loading: boolean = false;
  isOpen: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.checked) this.onChangeState();
  }

  //#region api

  getPhonebooksByParentIdApi() {
    if (this.phonebook.subItems?.length) {
      this.toggleVisibleSubItems(!this.isOpen);
      return;
    }
    this.loading = true;
    this.currentService
      .phonebooksGetPhonebooksByParentId({ ParentId: this.phonebook.id })
      .pipe(
        takeUntil(this.onDestroy$),
        delay(1000),
        finalize(() => {
          this.loading = false;
        })
      )
      .pipe(
        map((res) => {
          if (res.isSucceed) {
            const newModel: PhonebookCustomDto[] = res.data?.map((p) => ({
              ...p,
              isVisible: false,
              subItems: [],
            }));
            return newModel?.length ? newModel : null;
          } else return undefined;
        })
      )
      .subscribe((subPhonebooks) => {
        if (subPhonebooks !== undefined) {
          this.phonebookStateService.applySelectedPhonebooks(
            subPhonebooks,
            this.phonebookRecipients
          );
          this.phonebook.subItems = subPhonebooks;
          if (subPhonebooks) this.toggleVisibleSubItems(true);
          this.onChangeState();
        }
      });
  }

  //#endregion

  toggleVisibleSubItems(isVisible: boolean) {
    if (!this.phonebook?.subItems?.length) return;
    if (isVisible)
      this.phonebook.subItems = this.phonebook?.subItems?.map((item) => ({
        ...item,
        isVisible,
      }));
    else
      this.phonebook.subItems =
        this.phonebookStateService.deepChangePhonebooksState(
          this.phonebook.subItems,
          'isVisible',
          false
        );
    this.isOpen = isVisible;
    this.onChangeState();
  }

  onChecked(event) {
    this.phonebook.checked = event;
    if (this.phonebook.checked) {
      if (this.phonebook.contactsCount) {
        this.phonebook.fromIndex = this.phonebook.fromIndex || 1;
        this.phonebook.count =
          this.phonebook.count || this.phonebook.contactsCount;
        this.phonebook.isValid = event || undefined;
      } else this.phonebook.isValid = event || undefined;
    }
    this.onChangeState();
  }

  onCheckSubItems(checked) {
    if (!this.phonebook.checked) {
      this.phonebook.fromIndex = this.phonebook.contactsCount
        ? this.phonebook.fromIndex || 1
        : undefined;
      this.phonebook.count = checked
        ? this.phonebook.count || this.phonebook.contactsCount
        : undefined;
    }
    this.phonebook.isValid = checked || undefined;
    this.phonebook.checked = checked;
    // subItems
    this.phonebook.subItems.forEach((p) => {
      p.checked = checked;
      p.isValid = true;
      p.fromIndex = checked && p.contactsCount ? 1 : undefined;
      p.count = checked ? p.contactsCount : undefined;
    });
    this.onChangeState();
  }

  onOperation(operation: string) {
    this.operation.emit(operation);
  }

  onSelect() {
    this.select.emit(this.phonebook);
  }

  handleValidation() {
    this.phonebook.checked = true;
    this.phonebook.isValid = this.phonebookStateService.isValidPhonebook(
      this.phonebook
    );
    this.onChangeState();
  }

  isCheckedAllSubItems() {
    return this.phonebookStateService.isCheckedAllSubItems(
      this.phonebook?.subItems
    );
  }

  isCheckedSomeSubItems() {
    return this.phonebookStateService.isCheckedSomeSubItems(
      this.phonebook?.subItems
    );
  }

  onChangeState() {
    this.changeState.emit(this.phonebook);
  }
}
